// src/pages/ApiManager.jsx
import React from "react";
import { getCurrentUser } from "../utils/auth";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.portpilot.co";
const PROVIDERS = ["ILS", "WAYLI"];

const genId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
const ensureRowsHaveIds = (list = [], provider = "ILS") =>
  list.map((row) =>
    row && row._id
      ? row
      : {
          _id: genId(),
          provider,
          ...row,
        }
  );

const COLUMN_SPECS = [
  { key: "customer_code", label: "Cust Code*", width: 150, required: true },
  { key: "client_id", label: "Client ID*", width: 220, required: true },
  { key: "client_secret", label: "Client Secret*", width: 220, required: true },
  { key: "access_token", label: "Access Token", width: 220 },
  { key: "refresh_token", label: "Refresh Token", width: 220 },
  { key: "token_expires_at", label: "Token Expires At", width: 200, placeholder: "YYYY-MM-DD HH:MM:SS" },
  { key: "metaText", label: "Meta JSON", width: 260, isTextarea: true },
];

function normalizeMetaText(meta) {
  if (!meta || typeof meta !== "object") return "";
  try {
    if (Object.keys(meta).length === 0) return "";
    return JSON.stringify(meta);
  } catch {
    return "";
  }
}

function createEmptyRow(provider) {
  return {
    _id: genId(),
    provider,
    customer_code: "",
    client_id: "",
    client_secret: "",
    access_token: "",
    refresh_token: "",
    token_expires_at: "",
    metaText: "",
    persisted: false,
  };
}

export default function ApiManager() {
  const [provider, setProvider] = React.useState(PROVIDERS[0]);
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [selectedIds, setSelectedIds] = React.useState(new Set());
  const [deletedCodes, setDeletedCodes] = React.useState(new Set());

  const me = getCurrentUser();
  const userEmail = me?.email || "";
  const userRole = me?.role || "";

  const authHeaders = React.useMemo(() => {
    const headers = {};
    if (userEmail) headers["X-User-Email"] = userEmail;
    if (userRole) headers["X-User-Role"] = userRole;
    return headers;
  }, [userEmail, userRole]);

  const fetchRows = React.useCallback(async () => {
    setLoading(true);
    setError("");
    setSelectedIds(new Set());
    setDeletedCodes(new Set());
    try {
      const query = `?provider=${encodeURIComponent(provider)}`;
      const resp = await fetch(`${API_BASE}/api-credentials${query}`, { headers: authHeaders });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || data.ok === false) {
        throw new Error(data.error || "Load failed");
      }
      const list = Array.isArray(data.credentials) ? data.credentials : [];
      const normalized = ensureRowsHaveIds(
        list.map((row) => ({
          customer_code: row.customer_code || "",
          client_id: row.client_id || "",
          client_secret: row.client_secret || "",
          access_token: row.access_token || "",
          refresh_token: row.refresh_token || "",
          token_expires_at: row.token_expires_at || "",
          metaText: normalizeMetaText(row.meta),
          persisted: true,
        })),
        provider
      );
      setRows(normalized);
    } catch (err) {
      console.error(err);
      setError(err.message || "Load failed");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, provider]);

  React.useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const onAddRow = () => {
    setRows((prev) => [createEmptyRow(provider), ...prev]);
  };

  const toggleSelect = (rowId, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(rowId);
      else next.delete(rowId);
      return next;
    });
  };

  const onDeleteRows = () => {
    if (selectedIds.size === 0) return;
    const idsToDelete = new Set(selectedIds);
    const removedCodes = [];
    setRows((prev) => {
      const next = [];
      for (const row of prev) {
        if (idsToDelete.has(row._id)) {
          if (row.persisted && row.customer_code) {
            removedCodes.push(row.customer_code.toUpperCase());
          }
          continue;
        }
        next.push(row);
      }
      return next;
    });
    if (removedCodes.length > 0) {
      setDeletedCodes((existing) => {
        const updated = new Set(existing);
        removedCodes.forEach((code) => updated.add(code));
        return updated;
      });
    }
    setSelectedIds(new Set());
  };

  const onCellChange = (index, key, value) => {
    setRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [key]: value };
      return next;
    });
  };

  const duplicateCodes = React.useMemo(() => {
    const counts = new Map();
    for (const row of rows) {
      const code = (row.customer_code || "").trim().toUpperCase();
      if (!code) continue;
      counts.set(code, (counts.get(code) || 0) + 1);
    }
    return new Set([...counts.entries()].filter(([, count]) => count > 1).map(([code]) => code));
  }, [rows]);

  const onSave = async () => {
    setError("");
    const payloadRows = [];
    for (const row of rows) {
      const code = (row.customer_code || "").trim().toUpperCase();
      if (!code) {
        setError("Cust Code is required.");
        return;
      }
      if (duplicateCodes.has(code)) {
        setError("Cust Code must be unique per provider.");
        return;
      }
      const clientId = (row.client_id || "").trim();
      const clientSecret = (row.client_secret || "").trim();
      if (provider === "ILS" && (!clientId || !clientSecret)) {
        setError("Client ID and Client Secret are required for ILS credentials.");
        return;
      }
      const metaText = (row.metaText || "").trim();
      let meta = {};
      if (metaText) {
        try {
          const parsed = JSON.parse(metaText);
          if (parsed && typeof parsed === "object") {
            meta = parsed;
          } else {
            throw new Error("Meta must be a JSON object.");
          }
        } catch (err) {
          setError(`Invalid meta for ${code}: ${err.message || err}`);
          return;
        }
      }
      payloadRows.push({
        customer_code: code,
        client_id: clientId,
        client_secret: clientSecret,
        access_token: (row.access_token || "").trim(),
        refresh_token: (row.refresh_token || "").trim(),
        token_expires_at: (row.token_expires_at || "").trim(),
        meta,
      });
    }
    const deleteCodes = Array.from(deletedCodes).filter(Boolean);
    if (payloadRows.length === 0 && deleteCodes.length === 0) {
      setError("Nothing to save.");
      return;
    }

    setSaving(true);
    try {
      const resp = await fetch(`${API_BASE}/api-credentials`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders },
        body: JSON.stringify({
          provider,
          credentials: payloadRows,
          delete_codes: deleteCodes,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || data.ok === false) {
        throw new Error(data.error || "Save failed");
      }
      await fetchRows();
    } catch (err) {
      console.error(err);
      setError(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-bold">API Manager</h1>
        <label className="flex items-center gap-2 text-sm font-medium">
          Provider
          <select
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
            className="border rounded px-2 py-1"
            disabled={loading || saving}
          >
            {PROVIDERS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="px-3 py-1 rounded bg-gray-100 border"
          onClick={onAddRow}
          disabled={loading || saving}
        >
          + Add Row
        </button>
        <button
          type="button"
          className={
            selectedIds.size === 0
              ? "px-3 py-1 rounded bg-gray-200 text-gray-500 cursor-not-allowed"
              : "px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700"
          }
          onClick={onDeleteRows}
          disabled={selectedIds.size === 0 || saving}
        >
          Delete
        </button>
        <button
          type="button"
          className="px-3 py-1 rounded bg-blue-600 text-white disabled:opacity-50"
          onClick={onSave}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save"}
        </button>
        <button
          type="button"
          className="px-3 py-1 rounded border bg-white disabled:opacity-50"
          onClick={fetchRows}
          disabled={loading || saving}
        >
          Refresh
        </button>
        {(loading || saving) && <span className="text-sm text-gray-500">{loading ? "Loading..." : "Saving..."}</span>}
        {error && <span className="text-sm text-red-600">{error}</span>}
      </div>

      <div className="overflow-auto border rounded">
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr>
              <th className="sticky top-0 bg-white border-b px-2 py-2" style={{ width: 40, minWidth: 40 }}>
                &nbsp;
              </th>
              {COLUMN_SPECS.map((col) => (
                <th
                  key={col.key}
                  className="sticky top-0 bg-white border-b px-2 py-2 text-left font-semibold"
                  style={{ width: col.width, minWidth: col.width }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={COLUMN_SPECS.length + 1} className="text-center text-gray-500 py-8">
                  {loading ? "Loading..." : "No credentials found."}
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => {
                const code = (row.customer_code || "").trim().toUpperCase();
                const isDup = code && duplicateCodes.has(code);
                return (
                  <tr key={row._id} className="border-t align-top">
                    <td className="px-2 py-2" style={{ width: 40, minWidth: 40 }}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(row._id)}
                        onChange={(e) => toggleSelect(row._id, e.target.checked)}
                      />
                    </td>
                    {COLUMN_SPECS.map((col) => (
                      <td key={col.key} className="px-2 py-2" style={{ width: col.width }}>
                        {col.isTextarea ? (
                          <textarea
                            className="w-full border rounded px-2 py-1 text-sm"
                            rows={2}
                            placeholder={col.placeholder || ""}
                            value={row[col.key] ?? ""}
                            onChange={(e) => onCellChange(idx, col.key, e.target.value)}
                          />
                        ) : (
                          <input
                            className={`w-full border rounded px-2 py-1 text-sm ${
                              col.key === "customer_code" && isDup ? "border-red-500" : ""
                            }`}
                            placeholder={col.placeholder || ""}
                            value={row[col.key] ?? ""}
                            onChange={(e) => onCellChange(idx, col.key, e.target.value)}
                          />
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
